import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAppDispatch } from "@/hooks/storeHooks";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGetMeQuery } from "@/store/apis/userApi";
import { setUser } from "@/store/reducers/userSlice";
import { CheckCircle2Icon, Clock, Info } from "lucide-react";
import { PropsWithChildren, useEffect, useState } from "react";
import { IoMdRefresh } from "react-icons/io";
import { useNavigate } from "react-router";

export const PendingFooter = () => {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div dir="auto" className="mb-6">
      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
        <span>הזמן שחלף:</span>
        <span>{formatTime(timeElapsed)}</span>
      </div>

      <div dir="auto" className="flex items-center gap-2 text-sm text-gray-600">
        <Clock className="h-4 w-4" />
        <span>ממתינים לאישור מ-Peach. אין צורך לשלם שוב.</span>
      </div>
    </div>
  );
};

export const FailFooter = () => {
  return (
    <div dir="auto" className="mb-6">
      <ActionButton onClick={() => window.location.reload()}>
        <IoMdRefresh className="h-4 w-4 mr-2 group-hover:animate-spin duration-300 " />
        <span className="text-lg">נסה שוב</span>
      </ActionButton>
    </div>
  );
};

const successAlerts = {
  loading: {
    title: "מייבא נתוני משתמש",
    description: "זה יכול לקחת כמה שניות, נא להמתין...",
    icon: Clock,
  },
  success: {
    title: "הנתונים עודכנו בהצלחה!",
    description: "מה הלאה: תוכל כעת להכנס למערכת ",
    icon: CheckCircle2Icon,
  },
  failed: {
    title: "הייתה בעיה בעדכון הנתונים",
    description: "אנא נסה שוב מאוחר יותר או צור קשר עם התמיכה.",
    icon: Info,
  },
};

export const SuccessFooter = () => {
  const { data, isLoading, isError } = useGetMeQuery();
  const navigator = useNavigate();
  const dispatch = useAppDispatch();
  const [alert, setAlert] = useState(successAlerts.loading);

  useEffect(() => {
    if (isError) {
      setAlert(successAlerts.failed);
      return;
    }
    if (!data?.data.user) return;
    dispatch(setUser(data.data.user));
    setAlert(successAlerts.success);
  }, [data?.data, isError]);

  return (
    <div className="w-full flex flex-col items-center justify-center space-y-4">
      <Alert dir="auto">
        <alert.icon className="h-6 w-6 mr-2 text-green-500" />
        <AlertTitle>{alert.title}</AlertTitle>
        <AlertDescription>{alert.description}</AlertDescription>
      </Alert>
      <div className="w-full flex justify-center space-x-4 h-12 mx-auto">
        <ActionButton disabled={isLoading || isError} color="gray" onClick={() => navigator("/")}>
          <span className=" sm:text-lg">חזור לדף הבית</span>
        </ActionButton>

        <ActionButton disabled={isLoading || isError} onClick={() => navigator("/login")}>
          <span className="sm:text-lg">כניסה למערכת</span>
        </ActionButton>
      </div>
    </div>
  );
};

export const ActionButton = ({
  children,
  onClick,
  color = "zoooz",
  disabled,
}: PropsWithChildren & { onClick?: () => void; color?: "zoooz" | "gray"; disabled?: boolean }) => {
  const isMobile = useIsMobile();

  const colorOptions =
    color === "zoooz"
      ? "ring-zoooz-primary/20 bg-zoooz-primary/80 hover:bg-zoooz-primary"
      : "ring-gray-800/20 bg-gray-400/80 hover:bg-gray-800 hover:bg-gray-600/60";
  return (
    <div className="sm:w-44 mx-2 h-12 flex items-center justify-center container  ">
      <Button
        disabled={disabled}
        size={isMobile ? "sm" : "default"}
        onClick={onClick}
        className={`${colorOptions} group ring-1  ring-offset-2 hover:ring-6 duration-150 ease-in-out transition-all hover:ring-offset-0`}>
        {children}
      </Button>
    </div>
  );
};
